import { Z_INDEX } from "@/style/zIndex";
import { css } from "@emotion/react";
import { Typography } from "@/component/common/typography";

import { DESIGN_TOKEN_COLOR } from "@/style/designTokens";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import { useGetAllRetrospects } from "@/hooks/api/retrospect/useApiOptionsGetRetrospects";
import { LoadingSpinner } from "@/component/space/view/LoadingSpinner";
import RetrospectCard from "../RetrospectCard";

export default function InProgressRetrospectsWrapper() {
  // * 진행 중인 모든 회고 리스트 요청
  const { data: retrospects, isPending: isRetrospectsPending } = useGetAllRetrospects({
    select: (data) => data.retrospects.filter((retrospect) => retrospect.writeStatus === "PROCEEDING"),
  });

  return (
    <section
      css={css`
        margin-top: 3.2rem;
      `}
    >
      {/* ---------- 제목 ---------- */}
      <Typography variant="body15Bold" color="gray800">
        진행 중인 회고 ({retrospects?.length || 0})
      </Typography>

      {/* ---------- 진행 중인 회고 컨텐츠 ---------- */}
      <Swiper
        modules={[Navigation]}
        spaceBetween={12}
        slidesPerView={3}
        navigation
        css={css`
          padding-top: 1.2rem;
          width: 100%;
          position: relative;
          overflow-x: visible;
          overflow-y: hidden;

          /* 오른쪽 그라데이션 */
          &::after {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 8rem;
            height: 100%;
            background: linear-gradient(to left, ${DESIGN_TOKEN_COLOR.gray100}, rgba(255, 255, 255, 0));
            z-index: ${Z_INDEX.raised};
            pointer-events: none;
            opacity: 1;
            transition: opacity 0.3s ease;
          }

          /* 왼쪽 그라데이션 */
          &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 8rem;
            height: 100%;
            background: linear-gradient(to right, ${DESIGN_TOKEN_COLOR.gray100}, rgba(255, 255, 255, 0));
            z-index: ${Z_INDEX.raised};
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          /* 이전 버튼이 활성화되면 왼쪽 그라데이션 표시 */
          &:has(.swiper-button-prev:not(.swiper-button-disabled))::before {
            opacity: 1;
          }

          /* 다음 버튼이 비활성화되면 오른쪽 그라데이션 숨김 */
          &:has(.swiper-button-next.swiper-button-disabled)::after {
            opacity: 0;
          }

          .swiper-wrapper {
            align-items: stretch;
          }

          .swiper-slide {
            height: auto;
            display: flex;
          }

          .swiper-button-prev,
          .swiper-button-next {
            width: 4.8rem;
            height: 4.8rem;
            background: white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            border: 1px solid ${DESIGN_TOKEN_COLOR.gray200};
            top: 65%;
            transform: translateY(-50%);
            z-index: ${Z_INDEX.localOverlay};

            &::after {
              font-size: 1.6rem;
              font-weight: bold;
              color: ${DESIGN_TOKEN_COLOR.gray600};
            }

            &:hover {
              background: ${DESIGN_TOKEN_COLOR.gray100};
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }

            &.swiper-button-disabled {
              opacity: 0;
            }
          }

          .swiper-button-prev {
            left: 0.8rem;
          }

          .swiper-button-next {
            right: 0.8rem;
          }
        `}
      >
        {isRetrospectsPending ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 13.8rem;
              border-radius: 1.2rem;
              border: 1px dashed ${DESIGN_TOKEN_COLOR.gray500};
            `}
          >
            <LoadingSpinner />
          </div>
        ) : !retrospects || retrospects.length === 0 ? (
          <EmptyInProgressRetrospects />
        ) : (
          retrospects.map((retrospect) => (
            <SwiperSlide key={retrospect.retrospectId}>
              <RetrospectCard retrospect={retrospect} />
            </SwiperSlide>
          ))
        )}
      </Swiper>
    </section>
  );
}

function EmptyInProgressRetrospects() {
  return (
    <div
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        height: 13.8rem;
        border-radius: 1.6rem;
        background-color: ${DESIGN_TOKEN_COLOR.gray00};
      `}
    >
      <Typography variant="body15Medium" color="gray500">
        진행 중인 회고가 없어요
      </Typography>
    </div>
  );
}
